import React from "react";

import {
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";

import { parseCustomFieldValue } from "../../../../components/common/CustomFields";

import { CustomFieldsDisplay, Unit } from "../../../../utils/constants";
import { trimDecimal } from "../../../../utils/utils";
import {
  calculateMbf,
  calculateMsf,
  getPPU,
  getUnitName,
  isLumberCommodity,
  isPanelsCommodity,
} from "../../../../utils/loadTypeUtils";
import {
  AttachmentsBlock,
  HeaderBlock,
  InfoBlock,
  PhotosBlock,
  ReceivedAtBlock,
  SealsBlock,
} from "./InboundPODBody";
import { calcTotalQuantity } from "../InboundUtils";

const LineItemsBlock = ({ record, podInventories }) => {
  const inventoryCustomFields = (
    record?.account?.inventoryCustomFields || []
  ).filter((field) => field.display.includes(CustomFieldsDisplay.POD));
  const showProductCodeColumn = podInventories.some((lineItem) =>
    lineItem.productCode?.trim(),
  );
  const showPCSColumn = podInventories.some((lineItem) => lineItem.pcs != null);
  const pcsColumnName = record.totalUOM === Unit.UNIT ? "PPU" : "Pieces";
  const showFootageColumn = podInventories.some(
    (lineItem) => lineItem.footage != null,
  );

  return (
    <div style={{ width: "100%" }}>
      <Divider />
      <h3>
        <strong> INVENTORY RECEIVED </strong>
      </h3>
      <Table key="inboundInventory" style={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            {showProductCodeColumn && (
              <TableCell
                style={{ textAlign: "left", padding: "3px", width: "15%" }}
              >
                Product Code
              </TableCell>
            )}
            <TableCell
              style={{
                textAlign: "left",
                padding: "3px",
                width: `${showProductCodeColumn ? "35%" : "45%"}`, // Adjust dynamically
              }}
            >
              Product Name
            </TableCell>
            <TableCell
              style={{ textAlign: "right", padding: "3px", width: "10%" }}
            >
              {getUnitName(record.totalUOM)}
            </TableCell>
            {showPCSColumn && (
              <TableCell
                style={{ textAlign: "right", padding: "3px", width: "10%" }}
              >
                {pcsColumnName}
              </TableCell>
            )}
            {showFootageColumn && (
              <TableCell
                style={{ textAlign: "right", padding: "3px", width: "10%" }}
              >
                Footage
              </TableCell>
            )}
            {inventoryCustomFields.map((field, index) => (
              <TableCell
                key={"inv_cf_h_" + index}
                style={{
                  textAlign: "center",
                  padding: "3px",
                  width: `${100 / (inventoryCustomFields.length + 4)}%`,
                }} // Dynamically calculate custom field width
              >
                {field.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {podInventories.map((lineItem) => (
            <TableRow key={lineItem.id}>
              {showProductCodeColumn && (
                <TableCell
                  style={{ textAlign: "left", padding: "3px", width: "15%" }}
                >
                  {lineItem.productCode ? lineItem.productCode : "--"}
                </TableCell>
              )}
              <TableCell
                style={{
                  textAlign: "left",
                  padding: "3px",
                  width: `${showProductCodeColumn ? "35%" : "45%"}`, // Adjust dynamically
                }}
              >
                {lineItem.productName}
              </TableCell>
              <TableCell
                style={{ textAlign: "right", padding: "3px", width: "10%" }}
              >
                {lineItem.quantityReceived}
              </TableCell>
              {showPCSColumn && (
                <TableCell
                  style={{ textAlign: "right", padding: "3px", width: "10%" }}
                >
                  {lineItem.pcs != null ? lineItem.pcs : "--"}
                </TableCell>
              )}
              {showFootageColumn && (
                <TableCell
                  style={{ textAlign: "right", padding: "3px", width: "15%" }}
                >
                  {lineItem.footage
                    ? `${trimDecimal(
                        lineItem.footage * lineItem.quantityReceived,
                      ).toLocaleString()} ${lineItem.footageUOM}`
                    : "--"}
                </TableCell>
              )}
              {inventoryCustomFields.map((field, index) => {
                const customFieldValue = lineItem.customFields?.find((cf) =>
                  cf.startsWith(field.name),
                );

                return (
                  <TableCell
                    key={"inv_cf_" + index}
                    style={{
                      textAlign: "center",
                      padding: "3px",
                      width: `${100 / (inventoryCustomFields.length + 4)}%`, // Adjust dynamically
                    }}
                  >
                    {parseCustomFieldValue(customFieldValue)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const TotalsBlock = ({ record, podInventories }) => {
  const totalQuantity = calcTotalQuantity(record);

  // PCS Totals and Flags
  let totalPCS = 0;
  let allMissingPCS = true;
  let somePCSNull = false;

  // MBF Totals and Flags
  let totalMBF = 0;
  let allMissingMBF = true;
  let someMBFNull = false;

  // MSF Totals and Flags
  let totalMSF = 0;
  let allMissingMSF = true;
  let someMSFNull = false;

  podInventories.forEach((lineItem) => {
    // PCS Calculation
    if (lineItem.pcs == null) {
      somePCSNull = true;
    } else {
      allMissingPCS = false;
      if (record.totalUOM === Unit.UNIT) {
        totalPCS += lineItem.pcs * lineItem.quantityReceived;
      } else {
        totalPCS += lineItem.pcs;
      }
    }

    // Board Footage Calculations
    if (lineItem.footageUOM === Unit.MBF) {
      if (lineItem.footage == null) {
        someMBFNull = true;
      } else {
        allMissingMBF = false;
        totalMBF += lineItem.footage * lineItem.quantityReceived;
      }
    } else if (lineItem.footageUOM === Unit.MSF) {
      if (lineItem.footage == null) {
        someMSFNull = true;
      } else {
        allMissingMSF = false;
        totalMSF += lineItem.footage * lineItem.quantityReceived;
      }
    }
  });

  return (
    <div style={{ width: "100%" }}>
      <Divider />
      <Table id="inboundTotals" key="inboundTotals" style={{ width: "100%" }}>
        <TableBody>
          <TableRow key="totalUOM">
            <TableCell
              style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
            >
              Total {getUnitName(record.totalUOM)}
            </TableCell>
            <TableCell
              style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
            >
              {totalQuantity}
            </TableCell>
          </TableRow>
          {!allMissingPCS && (
            <TableRow key="totalPCS">
              <TableCell
                style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
              >
                Total Pieces
              </TableCell>
              <TableCell
                style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
              >
                {somePCSNull ? "N/A" : trimDecimal(totalPCS).toLocaleString()}
              </TableCell>
            </TableRow>
          )}
          {/* Display MBF total if there are lumber items */}
          {!allMissingMBF && (
            <TableRow key="totalMBF">
              <TableCell
                style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
              >
                Total MBF
              </TableCell>
              <TableCell
                style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
              >
                {someMBFNull
                  ? "N/A"
                  : `${trimDecimal(totalMBF).toLocaleString()} MBF`}
              </TableCell>
            </TableRow>
          )}
          {/* Display MSF total if there are panel items */}
          {!allMissingMSF && (
            <TableRow key="totalMSF">
              <TableCell
                style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
              >
                Total MSF
              </TableCell>
              <TableCell
                style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
              >
                {someMSFNull
                  ? "N/A"
                  : `${trimDecimal(totalMSF, 3).toLocaleString()} MSF`}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export const LumberPODBody = ({
  record,
  enclosePhotos,
  encloseAttachments,
}) => {
  const podInventories = record.inventories.items.map((lineItem) => {
    // Determine loadType to calculate appropriate values
    const loadType =
      lineItem.reloadYardCommodityId || record.reloadYardCommodityId;

    let footage = null;
    let footageUOM = "";

    if (isLumberCommodity(loadType)) {
      footage = calculateMbf(lineItem);
      footageUOM = Unit.MBF;
    } else if (isPanelsCommodity(loadType)) {
      footage = calculateMsf(lineItem);
      footageUOM = Unit.MSF;
    }

    return {
      ...lineItem,
      pcs: getPPU(lineItem),
      footage,
      footageUOM,
    };
  });

  return (
    <Paper elevation={0} style={{ maxWidth: "900px", padding: "10px" }}>
      <HeaderBlock />
      <InfoBlock record={record} />
      <ReceivedAtBlock record={record} />
      <LineItemsBlock record={record} podInventories={podInventories} />
      <TotalsBlock record={record} podInventories={podInventories} />
      <SealsBlock record={record} />
      {encloseAttachments && <AttachmentsBlock record={record} />}
      {enclosePhotos && <PhotosBlock record={record} />}
    </Paper>
  );
};
